import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import { LinkButtonComponent } from '../../link-button.component';
import { ReadonlyFieldRowComponent } from './readonly-field-row.component';
import type { OverviewRow } from './overview.types';

/**
 * One card on the read view's Overzicht tab (#21239, task 21250): a section heading plus the
 * flagged answers of that tab as label/value rows.
 *
 * Presentational — everything it shows arrives through inputs and the only thing it reports back
 * is the "Bekijk alles" click. The Formly side (`overview-tab.component.ts`) owns where the rows
 * come from and where the link jumps to; keeping those out of here is what makes the card
 * storyable without a form, a store or an injector (Storybook conventions, rule 1/2).
 *
 * Figma: there is NO overview-card design in the export (figma-leesweergave-spec §6 — searched
 * for widget summaries and for "Bekijk alles"/"View all", nothing found). The card is therefore
 * assembled out of the two building blocks that ARE documented: the entry-card head of
 * `pages.component.ts` (2652-117626) and the section head 1396:76013 — round 30px accent bubble,
 * 15px medium title — over the canonical Label-list grid (1413:72292: two columns, 16px gutter),
 * which `readonly-onboarding.component.ts` already renders as `sm:grid-cols-2 gap-x-5 gap-y-3`.
 * All colours come from `--dd-*`/`--fuse-*` tokens, so dark mode follows without a second rule.
 */
@Component({
  selector: 'dd-overview-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, LinkButtonComponent, ReadonlyFieldRowComponent],
  styles: `
    :host {
      display: block;
    }
  `,
  template: `
    <section
      class="w-full bg-card rounded-xl border border-[color:var(--dd-card-border)] py-6 px-6 flex flex-col gap-5"
      [attr.data-cy]="'overview-card-' + sectionKey()">
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          @if (icon(); as svgIcon) {
            <span
              class="inline-flex items-center justify-center flex-none w-[1.875rem] h-[1.875rem] rounded-full bg-[var(--dd-badge-accent-bg)]"
              aria-hidden="true">
              <mat-icon class="icon-size-4 text-[color:var(--dd-badge-accent-fg)]" [svgIcon]="svgIcon" />
            </span>
          }
          <h3 class="truncate text-[0.9375rem] font-medium leading-[1.3] text-default m-0">{{ label() }}</h3>
        </div>
        <!-- Only rendered when the parent can actually jump somewhere: a card whose section has
             nowhere to open (a form without tabs or pages) gets no link rather than a dead one. -->
        @if (seeAllLabel(); as linkLabel) {
          <button
            dd-link-button
            type="button"
            class="flex-none"
            [attr.data-cy]="'overview-see-all-' + sectionKey()"
            (click)="seeAll.emit()">
            {{ linkLabel }}
          </button>
        }
      </div>

      <!-- Same Label-list grid as the onboarding review card, so the two read surfaces line up. -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
        @for (row of rows(); track row.field) {
          <dd-readonly-field-row [field]="row.field" [value]="row.value" />
        }
      </div>
    </section>
  `,
})
export class OverviewCardComponent {
  readonly label = input.required<string>();
  readonly rows = input.required<OverviewRow[]>();
  /** Key of the tab/page this card summarises; only used to build stable `data-cy` hooks. */
  readonly sectionKey = input('');
  /** Optional icon for the section bubble (Figma 1396:76013); no icon renders no bubble. */
  readonly icon = input<string | null>(null);
  /** Text of the jump link, or `null` for a card with nowhere to jump to. */
  readonly seeAllLabel = input<string | null>(null);

  readonly seeAll = output<void>();
}
